import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Slider from "@mui/material/Slider";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Loading from "components/Loading";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import axios from "axios";
import Swal from "sweetalert2";
import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import { useSoftUIController } from "context";
import { figmaTokens } from "assets/theme/designTokens";
import { getUserDisplayName, getUserEmail } from "utils/userProfile";
import { importEstablishments } from "services/establishmentService";
import {
  createSecretTripleClickHandler,
  isAssistantBuildEnabled,
  useAssistantVisibility,
} from "utils/assistantVisibility";

const CSV_TEMPLATE_PATH = `${process.env.PUBLIC_URL || ""}/samples/establishments-import-template.csv`;

const CSV_COLUMNS = [
  { key: "ProvinceName", required: true, example: "Chari Baguirmi" },
  { key: "DepartmentName", required: true, example: "BAGUIRMI" },
  { key: "SubPrefectureName", required: true, example: "Bodoro" },
  { key: "Name", required: true, example: "ECOLE A" },
  { key: "Type", required: true, example: "School" },
  { key: "Status", required: true, example: "1" },
  { key: "Latitude", required: true, example: "12.123456" },
  { key: "Longitude", required: true, example: "15.123456" },
  { key: "Code", required: true, example: "E001" },
];

const swalApp = {
  confirmButtonColor: figmaTokens.accent,
  cancelButtonColor: figmaTokens.textMuted,
  background: figmaTokens.surface,
  color: figmaTokens.textPrimary,
};

const cardSx = {
  borderRadius: "16px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
  overflow: "hidden",
  height: "100%",
};

const sectionLabelSx = {
  display: "block",
  mb: 0.75,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: figmaTokens.textMuted,
};

const DEFAULT_RADIUS_METERS = 100;
const MAX_RADIUS_METERS = 100000;
const RADIUS_PRESETS = [50, 100, 250, 500, 1000];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function parseCsvPreview(text, maxRows = 5) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { headers: [], rows: [], valid: false, error: "empty" };

  const headers = lines[0].split(",").map((h) => h.trim());
  const required = CSV_COLUMNS.filter((c) => c.required).map((c) => c.key);
  const missing = required.filter((col) => !headers.includes(col));
  if (missing.length) {
    return { headers, rows: [], valid: false, error: "missing_columns", missing };
  }

  const rows = lines.slice(1, 1 + maxRows).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    return headers.reduce((acc, header, idx) => {
      acc[header] = values[idx] ?? "";
      return acc;
    }, {});
  });

  return { headers, rows, valid: true, totalRows: Math.max(0, lines.length - 1) };
}

function formatImportResult(data) {
  if (!data || typeof data !== "object") return null;
  return {
    message: data.message ?? data.Message ?? data.title ?? data.Title ?? null,
    imported: data.importedCount ?? data.ImportedCount ?? data.successCount ?? data.SuccessCount ?? null,
    failedCount: data.failedCount ?? data.FailedCount ?? data.failureCount ?? data.FailureCount ?? null,
    total: data.totalCount ?? data.TotalCount ?? data.total ?? data.Total ?? null,
    errors: data.errors ?? data.Errors ?? data.details ?? data.Details ?? null,
    isError: false,
  };
}

function Settings() {
  const { t, language, setLanguage } = useTranslate();
  const navigate = useNavigate();
  const [controller] = useSoftUIController();
  const user = controller?.user;
  const { toggle: toggleAssistant } = useAssistantVisibility();

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [radiusMeters, setRadiusMeters] = useState(String(DEFAULT_RADIUS_METERS));
  const [savedRadiusMeters, setSavedRadiusMeters] = useState(DEFAULT_RADIUS_METERS);
  const [attendancePolicy, setAttendancePolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState("");
  const [policySuccess, setPolicySuccess] = useState("");

  const displayName = useMemo(() => getUserDisplayName(user), [user]);
  const userEmail = useMemo(() => getUserEmail(user), [user]);
  const numericRadius = Number(radiusMeters);
  const radiusIsValid = Number.isFinite(numericRadius) && numericRadius > 0 && numericRadius <= MAX_RADIUS_METERS;
  const radiusIsDirty = radiusIsValid && numericRadius !== savedRadiusMeters;

  const loadAttendancePolicy = useCallback(async () => {
    setPolicyLoading(true);
    setPolicyError("");
    try {
      const response = await axios.get(`${apiurl}attendance-policy`, { headers: authHeaders() });
      const policy = response.data?.data ?? response.data;
      const nextRadius = Number(policy?.radiusMeters);
      const validRadius =
        Number.isFinite(nextRadius) && nextRadius > 0 && nextRadius <= MAX_RADIUS_METERS
          ? nextRadius
          : DEFAULT_RADIUS_METERS;
      setAttendancePolicy(policy || {});
      setRadiusMeters(String(validRadius));
      setSavedRadiusMeters(validRadius);
    } catch (err) {
      setPolicyError(
        err.response?.data?.message ||
          t("settings.radius.error_load", "Could not load the current attendance radius.")
      );
    } finally {
      setPolicyLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadAttendancePolicy();
  }, [loadAttendancePolicy]);

  const handleSaveRadius = async () => {
    if (!radiusIsValid) return;
    setPolicySaving(true);
    setPolicyError("");
    setPolicySuccess("");
    try {
      const response = await axios.put(
        `${apiurl}attendance-policy/radius`,
        { radiusMeters: numericRadius },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      const policy = response.data?.data ?? response.data;
      setAttendancePolicy((current) => ({ ...current, ...(policy || {}), radiusMeters: numericRadius }));
      setSavedRadiusMeters(numericRadius);
      setPolicySuccess(t("settings.radius.success", "Attendance radius updated successfully."));
    } catch (err) {
      setPolicyError(
        err.response?.data?.message ||
          t("settings.radius.error_save", "Could not update the attendance radius. Please try again.")
      );
    } finally {
      setPolicySaving(false);
    }
  };

  const handleAssistantSecretClick = useMemo(
    () =>
      createSecretTripleClickHandler(() => {
        if (!isAssistantBuildEnabled()) return;
        const enabled = toggleAssistant();
        if (!enabled && window.location.pathname.startsWith("/assistant")) {
          navigate("/dashboard", { replace: true });
        }
      }),
    [navigate, toggleAssistant]
  );

  const quickLinks = [
    {
      to: "/gestion-utilisateurs",
      icon: "group",
      label: t("nav.users", "User management"),
    },
    {
      to: "/notifications",
      icon: "notifications",
      label: t("nav.notifications", "Notifications"),
    },
  ];

  const resetFileState = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setPreviewError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleFileSelect = useCallback(
    async (file) => {
      setImportResult(null);
      if (!file) {
        resetFileState();
        return;
      }

      if (!file.name.toLowerCase().endsWith(".csv")) {
        setPreviewError(t("settings.import.error_csv_only", "Please select a .csv file."));
        resetFileState();
        return;
      }

      setSelectedFile(file);
      setPreviewError("");

      try {
        const text = await file.text();
        const parsed = parseCsvPreview(text);
        if (!parsed.valid) {
          if (parsed.error === "missing_columns") {
            setPreviewError(
              t("settings.import.error_missing_columns", "Missing required columns: {{cols}}").replace(
                "{{cols}}",
                (parsed.missing || []).join(", ")
              )
            );
          } else {
            setPreviewError(t("settings.import.error_empty", "The CSV file is empty."));
          }
          setPreview(null);
          return;
        }
        setPreview(parsed);
      } catch {
        setPreviewError(t("settings.import.error_read", "Could not read the CSV file."));
        setPreview(null);
      }
    },
    [resetFileState, t]
  );

  const handleImport = async () => {
    if (!selectedFile) {
      setPreviewError(t("settings.import.error_no_file", "Select a CSV file first."));
      return;
    }
    if (preview && !preview.valid) return;

    const confirm = await Swal.fire({
      title: t("settings.import.confirm_title", "Import establishments?"),
      text: t("settings.import.confirm_text", "This will upload « {{file}} » to the server.").replace(
        "{{file}}",
        selectedFile.name
      ),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("settings.import.upload", "Import"),
      cancelButtonText: t("settings.import.cancel", "Cancel"),
      ...swalApp,
    });
    if (!confirm.isConfirmed) return;

    setUploading(true);
    setImportResult(null);
    let importSucceeded = false;
    try {
      const data = await importEstablishments(selectedFile);

      const formatted = formatImportResult(data);
      setImportResult(formatted || { message: t("settings.import.success_generic", "Import completed.") });
      importSucceeded = true;
      resetFileState();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.response?.data?.error ||
        t("settings.import.error_upload", "Import failed. Check the CSV format and try again.");
      setImportResult({ message: msg, isError: true });
      await Swal.fire({
        title: t("settings.import.error_title", "Import failed"),
        text: msg,
        icon: "error",
        ...swalApp,
      });
    } finally {
      setUploading(false);
    }

    if (importSucceeded) {
      await Swal.fire({
        title: t("settings.import.success_title", "Import successful"),
        text: t("settings.import.success_redirect", "Establishments were imported. Opening the list…"),
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        ...swalApp,
      });
      navigate("/establishments");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {uploading && <Loading />}
      <SoftBox py={3}>
        <SoftBox mb={3} px={{ xs: 2, md: 3 }}>
          <SoftTypography
            variant="caption"
            fontWeight="bold"
            onClick={handleAssistantSecretClick}
            sx={{
              color: figmaTokens.accent,
              letterSpacing: "0.12em",
              display: "block",
              mb: 0.5,
              userSelect: "none",
            }}
          >
            {t("settings.page_kicker", "ADMINISTRATION")}
          </SoftTypography>
          <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mb: 0.5 }}>
            {t("settings.title", "Settings")}
          </SoftTypography>
          <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, maxWidth: 720 }}>
            {t("settings.page_hint", "Manage your account preferences and import establishment data.")}
          </SoftTypography>
        </SoftBox>

        <SoftBox px={{ xs: 2, md: 3 }}>
          <Card variant="outlined" sx={{ ...cardSx, mb: 2.5 }}>
            <SoftBox
              sx={{
                p: { xs: 2, md: 2.5 },
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Avatar sx={{ width: 52, height: 52, bgcolor: figmaTokens.accent, fontWeight: 800 }}>
                  {(displayName !== "—" ? displayName : userEmail).charAt(0).toUpperCase()}
                </Avatar>
                <SoftBox sx={{ minWidth: 0 }}>
                  <SoftTypography sx={sectionLabelSx}>{t("settings.account.title", "Account")}</SoftTypography>
                  <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, lineHeight: 1.2 }}>
                    {displayName}
                  </SoftTypography>
                  <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, wordBreak: "break-all" }}>
                    {userEmail}
                  </SoftTypography>
                </SoftBox>
              </Stack>
              <SoftBox>
                <SoftTypography sx={{ ...sectionLabelSx, mb: 0.75 }}>{t("navbar.language", "Language")}</SoftTypography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={language}
                  onChange={(_, value) => value && setLanguage(value)}
                >
                  <ToggleButton value="fr">FR</ToggleButton>
                  <ToggleButton value="en">EN</ToggleButton>
                </ToggleButtonGroup>
              </SoftBox>
            </SoftBox>
            <Divider />
            <SoftBox sx={{ px: { xs: 2, md: 2.5 }, py: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {quickLinks.map((item) => (
                <SoftButton
                  key={item.to}
                  component={Link}
                  to={item.to}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<Icon fontSize="small">{item.icon}</Icon>}
                >
                  {item.label}
                </SoftButton>
              ))}
            </SoftBox>
          </Card>

          <Card variant="outlined" sx={{ ...cardSx, height: "auto", mb: 2.5 }}>
            <SoftBox
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 2,
                borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                  {t("settings.radius.title", "Attendance radius")}
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                  {t("settings.radius.subtitle", "Set the preferred check-in distance for the mobile app.")}
                </SoftTypography>
              </SoftBox>
              <Chip
                size="small"
                color="success"
                icon={<Icon sx={{ fontSize: "16px !important" }}>location_on</Icon>}
                label={`${savedRadiusMeters.toLocaleString()} m`}
                sx={{ fontWeight: 700 }}
              />
            </SoftBox>

            <SoftBox p={{ xs: 2, md: 2.5 }}>
              {policyLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: "center" }}>
                  <CircularProgress size={22} />
                  <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary }}>
                    {t("settings.radius.loading", "Loading attendance policy…")}
                  </SoftTypography>
                </Stack>
              ) : (
                <Grid container spacing={3} alignItems="stretch">
                  <Grid item xs={12} md={4}>
                    <SoftBox
                      sx={{
                        minHeight: 210,
                        height: "100%",
                        borderRadius: "14px",
                        bgcolor: "rgba(23, 193, 232, 0.07)",
                        border: "1px solid rgba(23, 193, 232, 0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {[170, 122, 74].map((size) => (
                        <SoftBox
                          key={size}
                          sx={{
                            position: "absolute",
                            width: size,
                            height: size,
                            borderRadius: "50%",
                            border: "1px solid rgba(23, 193, 232, 0.28)",
                            bgcolor: size === 74 ? "rgba(23, 193, 232, 0.12)" : "transparent",
                          }}
                        />
                      ))}
                      <SoftBox sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
                        <Icon sx={{ color: figmaTokens.accent, fontSize: 28 }}>my_location</Icon>
                        <SoftTypography variant="h5" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                          {radiusIsValid ? numericRadius.toLocaleString() : "—"}
                        </SoftTypography>
                        <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                          {t("settings.radius.meters", "meters")}
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <SoftTypography sx={sectionLabelSx}>
                      {t("settings.radius.preferred_distance", "Preferred distance")}
                    </SoftTypography>
                    <TextField
                      fullWidth
                      type="number"
                      value={radiusMeters}
                      onChange={(event) => {
                        setRadiusMeters(event.target.value);
                        setPolicyError("");
                        setPolicySuccess("");
                      }}
                      inputProps={{ min: 1, max: MAX_RADIUS_METERS, step: 1 }}
                      error={radiusMeters !== "" && !radiusIsValid}
                      helperText={
                        !radiusIsValid
                          ? t("settings.radius.validation", "Enter a value from 1 to 100,000 meters.")
                          : t("settings.radius.input_hint", "Allowed range: 1–100,000 meters")
                      }
                      InputProps={{
                        endAdornment: (
                          <SoftTypography variant="body2" fontWeight="bold" sx={{ color: figmaTokens.textMuted }}>
                            m
                          </SoftTypography>
                        ),
                      }}
                    />
                    <Slider
                      value={radiusIsValid ? Math.min(numericRadius, MAX_RADIUS_METERS) : 1}
                      min={1}
                      max={MAX_RADIUS_METERS}
                      step={1}
                      onChange={(_, value) => {
                        setRadiusMeters(String(value));
                        setPolicyError("");
                        setPolicySuccess("");
                      }}
                      sx={{ mt: 2, mb: 0.5 }}
                    />
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                      {RADIUS_PRESETS.map((preset) => (
                        <Chip
                          key={preset}
                          size="small"
                          label={preset >= 1000 ? `${preset / 1000} km` : `${preset} m`}
                          color={numericRadius === preset ? "info" : "default"}
                          variant={numericRadius === preset ? "filled" : "outlined"}
                          onClick={() => {
                            setRadiusMeters(String(preset));
                            setPolicyError("");
                            setPolicySuccess("");
                          }}
                          sx={{ cursor: "pointer", fontWeight: 600 }}
                        />
                      ))}
                    </Stack>
                    <Alert severity="info" icon={<Icon fontSize="small">info</Icon>} sx={{ mb: 2 }}>
                      {t(
                        "settings.radius.future_only",
                        "This value applies to future attendance records. Existing records keep the radius captured when they were created."
                      )}
                    </Alert>
                    {policyError && <Alert severity="error" sx={{ mb: 2 }}>{policyError}</Alert>}
                    {policySuccess && <Alert severity="success" sx={{ mb: 2 }}>{policySuccess}</Alert>}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                      <SoftButton
                        variant="gradient"
                        color="info"
                        onClick={handleSaveRadius}
                        disabled={!radiusIsDirty || policySaving}
                        startIcon={policySaving ? <CircularProgress size={15} color="inherit" /> : <Icon>save</Icon>}
                      >
                        {policySaving
                          ? t("settings.radius.saving", "Saving…")
                          : t("settings.radius.save", "Save radius")}
                      </SoftButton>
                      {attendancePolicy?.updatedOn && (
                        <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                          {t("settings.radius.last_updated", "Last updated")}: {new Date(attendancePolicy.updatedOn).toLocaleString(language)}
                          {attendancePolicy.updatedBy ? ` · ${attendancePolicy.updatedBy}` : ""}
                        </SoftTypography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </SoftBox>
          </Card>

          <Card variant="outlined" sx={cardSx}>
            <SoftBox
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 2,
                borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                  {t("settings.import.title", "Import establishments")}
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                  {t("settings.import.subtitle", "Upload a CSV file to bulk-import establishments.")}
                </SoftTypography>
              </SoftBox>
              <SoftButton
                variant="outlined"
                color="secondary"
                size="small"
                component="a"
                href={CSV_TEMPLATE_PATH}
                download="establishments-import-template.csv"
              >
                {t("settings.import.download_template", "Download template")}
              </SoftButton>
            </SoftBox>

            <SoftBox p={{ xs: 2, md: 2.5 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} lg={5}>
                  <Alert severity="info" icon={<Icon fontSize="small">info</Icon>} sx={{ mb: 2 }}>
                    {t(
                      "settings.import.hint",
                      "CSV must include province, department, sub-prefecture, establishment name, type, status, coordinates and code."
                    )}
                  </Alert>

                  <SoftBox
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleFileSelect(file);
                    }}
                    sx={{
                      border: `2px dashed ${figmaTokens.borderStrong}`,
                      borderRadius: "14px",
                      p: 3,
                      textAlign: "center",
                      bgcolor: "rgba(247, 249, 252, 0.85)",
                    }}
                  >
                    <Icon sx={{ fontSize: 40, color: figmaTokens.accent, mb: 1 }}>upload_file</Icon>
                    <SoftTypography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {selectedFile
                        ? selectedFile.name
                        : t("settings.import.drop_hint", "Drag & drop a CSV file here")}
                    </SoftTypography>
                    <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, display: "block", mb: 1.5 }}>
                      {t("settings.import.or_browse", "or browse from your computer")}
                    </SoftTypography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      hidden
                      onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
                    />
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                      <SoftButton variant="outlined" color="secondary" onClick={() => fileInputRef.current?.click()}>
                        {t("settings.import.browse", "Browse CSV")}
                      </SoftButton>
                      {selectedFile && (
                        <SoftButton variant="text" color="secondary" onClick={resetFileState}>
                          {t("settings.import.clear", "Clear")}
                        </SoftButton>
                      )}
                    </Stack>
                  </SoftBox>

                  {previewError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {previewError}
                    </Alert>
                  )}

                  {importResult && (
                    <Alert severity={importResult.isError ? "error" : "success"} sx={{ mt: 2 }}>
                      {importResult.message}
                      {importResult.imported != null && (
                        <>
                          {" "}
                          — {t("settings.import.result_imported", "Imported")}: {importResult.imported}
                        </>
                      )}
                      {importResult.failedCount != null && (
                        <>
                          {" "}
                          — {t("settings.import.result_failed", "Failed")}: {importResult.failedCount}
                        </>
                      )}
                    </Alert>
                  )}

                  <SoftButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleImport}
                    disabled={uploading || !selectedFile || !preview?.valid}
                  >
                    {uploading
                      ? t("settings.import.uploading", "Importing…")
                      : t("settings.import.upload", "Import establishments")}
                  </SoftButton>
                </Grid>

                <Grid item xs={12} lg={7}>
                  {preview?.valid && (
                    <SoftBox mb={2.5}>
                      <SoftTypography sx={{ ...sectionLabelSx, mb: 1 }}>
                        {t("settings.import.preview", "Preview")} ({preview.totalRows}{" "}
                        {t("settings.import.rows", "rows")})
                      </SoftTypography>
                      <SoftBox sx={{ overflowX: "auto", border: `1px solid ${figmaTokens.borderSubtle}`, borderRadius: "12px" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {preview.headers.map((header) => (
                                <TableCell key={header} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                                  {header}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {preview.rows.map((row, idx) => (
                              <TableRow key={idx}>
                                {preview.headers.map((header) => (
                                  <TableCell key={header} sx={{ whiteSpace: "nowrap" }}>
                                    {row[header] || "—"}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </SoftBox>
                    </SoftBox>
                  )}

                  <SoftTypography sx={{ ...sectionLabelSx, mb: 1 }}>
                    {t("settings.import.columns_title", "Required CSV columns")}
                  </SoftTypography>
                  <SoftBox sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>{t("settings.import.column", "Column")}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>{t("settings.import.example", "Example")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {CSV_COLUMNS.map((col) => (
                          <TableRow key={col.key}>
                            <TableCell sx={{ fontWeight: 600 }}>{col.key}</TableCell>
                            <TableCell sx={{ color: figmaTokens.textSecondary }}>{col.example}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </SoftBox>
                </Grid>
              </Grid>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Settings;
